import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  HiOutlineCheckCircle,
  HiOutlineEllipsisVertical,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlinePencilSquare,
} from "react-icons/hi2";

export const ROW_ACTIONS_MENU_W = 220;
export const ROW_ACTIONS_MENU_H = 216;

export function useAdminRowActionsMenu() {
  const [actionMenu, setActionMenu] = useState(null);

  useEffect(() => {
    if (!actionMenu) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setActionMenu(null);
    };
    const onScroll = () => setActionMenu(null);
    const onResize = () => setActionMenu(null);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [actionMenu]);

  const close = useCallback(() => setActionMenu(null), []);

  const toggle = useCallback((e, row) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setActionMenu((prev) => {
      if (prev && prev.row?.id === row?.id) return null;
      const pad = 10;
      let left = rect.right - ROW_ACTIONS_MENU_W;
      let top = rect.bottom + 6;
      left = Math.max(pad, Math.min(left, window.innerWidth - ROW_ACTIONS_MENU_W - pad));
      if (top + ROW_ACTIONS_MENU_H > window.innerHeight - pad) {
        top = rect.top - ROW_ACTIONS_MENU_H - 6;
      }
      top = Math.max(pad, top);
      return { row, top, left };
    });
  }, []);

  const isOpen = useCallback(
    (row) => actionMenu?.row?.id != null && row?.id != null && actionMenu.row.id === row.id,
    [actionMenu]
  );

  return { actionMenu, setActionMenu, close, toggle, isOpen };
}

export function AdminRowActionsTrigger({ row, rowLabel = "row", isOpen, onToggle, disabled = false }) {
  return (
    <button
      type="button"
      className={`adm-ach-actions-trigger${isOpen ? " adm-ach-actions-trigger--open" : ""}`}
      aria-label={`Actions for ${rowLabel}`}
      aria-haspopup="menu"
      aria-expanded={isOpen}
      disabled={disabled}
      onClick={(e) => onToggle(e, row)}
    >
      <HiOutlineEllipsisVertical className="adm-ach-actions-dots" aria-hidden />
    </button>
  );
}

export function AdminRowActionsMenu({
  actionMenu,
  onClose,
  ariaLabel = "Row actions",
  isActive = false,
  disabled = false,
  onView,
  onUpdate,
  onToggleActive,
  showActiveToggle = true,
}) {
  if (!actionMenu) return null;

  const run = (fn) => {
    if (typeof fn === "function") fn(actionMenu.row);
    onClose();
  };

  return createPortal(
    <>
      <div className="adm-ach-row-menu-backdrop" aria-hidden="true" onClick={onClose} />
      <div
        className="adm-ach-row-menu"
        role="menu"
        aria-label={ariaLabel}
        style={{ top: actionMenu.top, left: actionMenu.left, width: ROW_ACTIONS_MENU_W }}
      >
        {onView ? (
          <button
            type="button"
            role="menuitem"
            className="adm-ach-row-menu-item"
            disabled={disabled}
            onClick={() => run(onView)}
          >
            <HiOutlineEye className="adm-ach-row-menu-icon" aria-hidden />
            View
          </button>
        ) : null}
        {onUpdate ? (
          <button
            type="button"
            role="menuitem"
            className="adm-ach-row-menu-item"
            disabled={disabled}
            onClick={() => run(onUpdate)}
          >
            <HiOutlinePencilSquare className="adm-ach-row-menu-icon" aria-hidden />
            Update
          </button>
        ) : null}
        {showActiveToggle && onToggleActive ? (
          isActive ? (
            <button
              type="button"
              role="menuitem"
              className="adm-ach-row-menu-item"
              disabled={disabled}
              onClick={() => run(onToggleActive)}
            >
              <HiOutlineEyeSlash className="adm-ach-row-menu-icon" aria-hidden />
              Inactive
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              className="adm-ach-row-menu-item adm-ach-row-menu-item--primary"
              disabled={disabled}
              onClick={() => run(onToggleActive)}
            >
              <HiOutlineCheckCircle className="adm-ach-row-menu-icon" aria-hidden />
              Active
            </button>
          )
        ) : null}
      </div>
    </>,
    document.body
  );
}

/** Standard actions column for AdminDataTable */
export function buildActionsColumn({ rowActions, getRowLabel, isRowDisabled }) {
  return {
    id: "actions",
    header: "Actions",
    width: "52px",
    align: "center",
    thClassName: "adm-th-actions",
    tdClassName: "adm-td-actions",
    render: (row) => (
      <AdminRowActionsTrigger
        row={row}
        rowLabel={getRowLabel(row)}
        isOpen={rowActions.isOpen(row)}
        onToggle={rowActions.toggle}
        disabled={isRowDisabled?.(row)}
      />
    ),
  };
}
